import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'

import Button from '../components/Button'
import GridHeader from '../components/GridHeader'
import SelectionBar from '../components/selection/SelectionBar'
import DialogRendiconto from '../components/DialogRendiconto'
import SortableTableHead from '../components/SortableTableHead'
import ButtonRunDownload from '../components/ButtonRunDownload'
import DialogRendicontoAdd from '../components/DialogRendicontoAdd'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import {
  colours,
  titlesRendiconto as titles,
  phrasesRendiconto as phrases,
} from '../settings/settings'
import { selectRendicontiSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import { useRunAlgorithm } from '../hooks/algoritmi-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetImpostazioniAvanzateQuery,
  useGetRendicontiQuery,
  useCreateRendicontoMutation,
  useUpdateRendicontoMutation,
  useUpgradeRendicontoMutation,
} from '../hooks/api-slice'
import { sumFun, formatEuro } from '../hooks/utils-functions'

const AVANZ_FIELDS = ['RendicontiIban', 'RendicontiBeneficiario']

const Rendiconti = () => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)

  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const iban = avanzate?.RendicontiIban
  const bene = avanzate?.RendicontiBeneficiario

  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !admin,
  })
  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const selectedSocieta = useSelector(state => state.selection.selectedSocieta)
  const [mode, setMode] = useState()
  const [rendiconto, setRendiconto] = useState()
  const [idGareArbitro, setIdGareArbitro] = useState()
  const [idGareRitardo, setIdGareRitardo] = useState()

  const urlStagione = `/stagione/${selectedStagione}`
  const urlSuffix = admin ? urlStagione : `/societa/${codiceUtente}`
  const { isLoading } = useGetRendicontiQuery(urlSuffix, {
    skip: !(admin ? selectedStagione : codiceUtente),
  })
  const rendiconti = useSelector(state =>
    selectRendicontiSorted(state, urlSuffix),
  )

  const [createRendicontoMutation] = useCreateRendicontoMutation()
  const [updateRendicontoMutation] = useUpdateRendicontoMutation()
  const [upgradeRendicontoMutation] = useUpgradeRendicontoMutation()

  document.title = `${config?.name} - Rendiconti`

  let columnsRendiconti = [{ label: 'Stagione', sortable: false }]
  if (admin)
    columnsRendiconti = [
      ...columnsRendiconti,
      { label: 'Codice', sortable: false },
      { label: 'Società', sortable: true, sortingField: 'nomeSocieta' },
    ]
  columnsRendiconti = [
    ...columnsRendiconti,
    { label: 'Totale', sortable: admin, sortingField: 'totaleDovuto' },
    { label: 'Pagato', sortable: false },
    { label: 'Da pagare', sortable: false },
    { label: 'Bilancio', sortable: admin, sortingField: 'balance' },
    { label: 'Opzioni', sortable: false },
  ]

  const rendicontiN = rendiconti?.filter(r =>
    admin && selectedSocieta
      ? r.codiceSocieta ===
        affiliazioniLight?.find(a => a.id === selectedSocieta)?.codiceSocieta
      : true,
  )

  const [openRendiconto, setOpenRendiconto] = useState()
  const [openRendicontoAdd, setOpenRendicontoAdd] = useState()

  const { setFeedback } = useFeedback()
  const { runAlgorithm } = useRunAlgorithm()

  const handleClickOpenRendiconto = id => {
    setMode(admin ? 'update' : 'view')
    setRendiconto(rendicontiN?.find(r => r.id === id))
    setIdGareArbitro(
      rendicontiN
        ?.find(r => r.id === id)
        ?.voci?.filter(
          v =>
            v.tipologia === 'needsReferee' || v.tipologia === 'requiresReferee',
        )
        ?.map(v => v.idGara),
    )
    setIdGareRitardo(
      rendicontiN
        ?.find(r => r.id === id)
        ?.voci?.filter(v => v.tipologia === 'risultato-ritardo')
        ?.map(v => v.idGara),
    )
    setOpenRendiconto(true)
  }

  const handleClickOpenRendicontoAdd = () => setOpenRendicontoAdd(true)

  const handleCloseRendiconto = () => {
    setOpenRendiconto()
    setMode()
    setRendiconto()
    setIdGareArbitro()
    setIdGareRitardo()
  }

  const handleCloseRendicontoAdd = () => setOpenRendicontoAdd()

  const confirmRendiconto = async x => {
    if (mode === 'update') await updateRendiconto(x)
    if (mode === 'view') handleCloseRendiconto()
  }

  const totTotaleDovuto = () =>
    formatEuro(sumFun(rendicontiN?.map(r => r.totaleDovuto)) || 0)
  const totTotalePagato = () =>
    formatEuro(sumFun(rendicontiN?.map(r => r.totalePagato)) || 0)

  const addRendiconto = async codiceSocieta => {
    const body = { codiceSocieta, stagione: selectedStagione }
    const result = await createRendicontoMutation(body)
    if (!result.error) {
      handleCloseRendicontoAdd()
      setFeedback(phrases.phraseAddSuccess, false, titles.titleAddSuccess)
    }
  }

  const upgradeRendiconto = async id => {
    const result = await upgradeRendicontoMutation(id)
    if (!result.error) handleCloseRendiconto()
  }

  const updateRendiconto = async rendiconto => {
    const arg = { id: rendiconto?._id, voci: rendiconto?.voci }
    const result = await updateRendicontoMutation(arg)
    if (!result.error) {
      handleCloseRendiconto()
      setFeedback(phrases.phraseUpdateSuccess, false, titles.titleUpdateSuccess)
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Rendiconti' />
      {admin && (
        <Grid container alignItems='center' sx={{ width: '100%' }}>
          <SelectionBar yStagione ySocieta />
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <Button onClick={handleClickOpenRendicontoAdd}>
                Nuovo rendiconto
              </Button>
            </center>
          </Grid>
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <ButtonRunDownload
                buttonText='Scarica tutti'
                algorithm='15_GenerateExcelRendiconto'
                type='XLSX'
                disabled={!selectedStagione || !rendicontiN?.length}
              />
            </center>
          </Grid>
        </Grid>
      )}
      {admin && rendicontiN && (
        <Grid container alignItems='center' sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <center>
              Totale dovuto: <b>{totTotaleDovuto()}</b>
            </center>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <center>
              Totale pagato: <b>{totTotalePagato()}</b>
            </center>
          </Grid>
        </Grid>
      )}
      {bene && iban && (
        <Grid size={12}>
          I pagamenti vanno effettuati a <b>{bene}</b> (IBAN <b>{iban}</b>).
        </Grid>
      )}
      {(isLoading && <GridCircularProgress />) ||
        (!rendiconti && (
          <GridHeader text='Elenco dei rendiconti non trovato.' />
        )) ||
        (!rendicontiN?.length && (
          <GridHeader text='Nessun rendiconto trovato.' />
        )) || (
          <Grid container sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={rendicontiN}
                  columns={columnsRendiconti}
                  tableName='rendiconti'
                />
                <TableBody>
                  {rendicontiN?.map(r => {
                    const isInDebit = r.balance < 0
                    const isInCredit = r.balance > 0
                    const isBalanced = r.balance === 0

                    return (
                      <TableRow
                        key={r.id}
                        sx={{
                          cursor: 'pointer',
                          ':hover': { backgroundColor: colours.greyVeryLight },
                        }}
                        onClick={() => handleClickOpenRendiconto(r.id)}
                      >
                        <TableCell>{r?.stagione}</TableCell>
                        {admin && <TableCell>{r?.codiceSocieta}</TableCell>}
                        {admin && <TableCell>{r?.nomeSocieta}</TableCell>}
                        <TableCell>
                          {formatEuro(r?.totaleDovuto || '-')}
                        </TableCell>
                        <TableCell>
                          {formatEuro(r?.totalePagato || '-')}
                        </TableCell>
                        <TableCell>
                          {formatEuro(r?.totaleDovuto - r?.totalePagato || '-')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            variant={isInDebit ? 'filled' : 'outlined'}
                            label={
                              (isInDebit && 'da pagare') ||
                              (isInCredit && 'in credito') ||
                              (isBalanced && 'in pari') ||
                              '??'
                            }
                            color={
                              (isInDebit && 'warning') ||
                              (isInCredit && 'info') ||
                              (isBalanced && 'success') ||
                              'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Grid container sx={{ width: '100%' }}>
                            <Grid size='grow'>
                              <IconButton
                                style={{ color: colours.greenExcel }}
                                onClick={e => {
                                  e.stopPropagation()
                                  runAlgorithm({
                                    algorithm: '15_GenerateExcelRendiconto',
                                    selectedRendiconto: r._id,
                                    exactlyOne: true,
                                    type: 'XLSX',
                                  })
                                }}
                              >
                                <Tooltip title='Scarica Excel'>
                                  <DownloadIcon />
                                </Tooltip>
                              </IconButton>
                            </Grid>
                            {admin && (
                              <Grid size='grow'>
                                <IconButton
                                  style={{ color: colours.greyLight }}
                                  onClick={e => {
                                    e.stopPropagation()
                                    upgradeRendiconto(r._id)
                                  }}
                                >
                                  <Tooltip title='Aggiorna'>
                                    <RefreshIcon />
                                  </Tooltip>
                                </IconButton>
                              </Grid>
                            )}
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <DialogRendiconto
        open={openRendiconto}
        handleClose={handleCloseRendiconto}
        triggerFunction={confirmRendiconto}
        rendicontoInit={rendiconto}
        idGareArbitro={idGareArbitro}
        idGareRitardo={idGareRitardo}
        mode={mode}
      />
      <DialogRendicontoAdd
        open={openRendicontoAdd}
        handleClose={handleCloseRendicontoAdd}
        triggerFunction={addRendiconto}
      />
    </Grid>
  )
}

export default Rendiconti
